import { useEffect, useRef } from 'react';
import type { RefObject, TextareaHTMLAttributes } from 'react';

interface AutoTextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  rows?: number;
  /** React 19: ref is a regular prop — callers may need the DOM node (e.g. the
   *  promo bio formatting bar reads selectionStart/selectionEnd). */
  ref?: RefObject<HTMLTextAreaElement | null>;
}

/**
 * Single-line-by-default textarea that auto-grows to fit its content, so
 * long values are never truncated like a fixed <input type="text">.
 */
export default function AutoTextarea({ value, onChange, rows = 1, className, ref: externalRef, ...rest }: AutoTextareaProps) {
  const innerRef = useRef<HTMLTextAreaElement>(null);
  const ref = externalRef ?? innerRef;

  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    resize();
  }, [value]);

  useEffect(() => {
    resize();
  }, []);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      rows={rows}
      className={className ? `auto-textarea ${className}` : 'auto-textarea'}
      {...rest}
    />
  );
}
